import { postcodeAreas } from './postcodeAreas'; 
import { pathHasSequence } from './utils/pathUtils';
import { lastNGamesArePerfect } from './utils/historyUtils';
import { ACHIEVEMENTS_KEY, canonEdge, readJSON, writeJSON } from './utils/storageUtils';


const STREAK_KEY_V2 = (d) => `pp_daily_streak_v2_${d}`;   // per-difficulty

const getJSON = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || 'null') ?? d; } catch { return d; } };

const HISTORY_KEY = 'pp_history_v2';
const VISITED_KEY = 'pp_visited_areas_v1';

function buildHistoryIfMissing(h) {
  if (h && h.totalGames != null && h.totalWins != null) return h;
  const db = readJSON(HISTORY_KEY, { games: [] });
  return {
    totalGames: db.games.length,
    totalWins:  db.games.filter(g => g.won).length,
  };
}

function buildMetaIfMissing(m) {
  const meta = { ...(m || {}) };
  if (meta.totalAreas == null) {
    meta.totalAreas = postcodeAreas ? Object.keys(postcodeAreas).length : 0;
  }
  if (meta.visitedCount == null) {
    const v = readJSON(VISITED_KEY, []);
    meta.visitedCount =
      Array.isArray(v) ? v.length :
      (v && typeof v === 'object') ? Object.keys(v).length : 0;
  }
  return meta;
}

function unlockAndPersist(list) {
  if (!list?.length) return [];
  const map = getJSON(ACHIEVEMENTS_KEY, {}) || {};
  const now = new Date().toISOString();
  const fresh = [];
  for (const a of list) {
    if (!a?.id) continue;
    const category = getAchievementCategory(a);
    if (!map[a.id]) { map[a.id] = { ...a, category, unlockedAt: now }; fresh.push({ ...a, category }); }
  }
  if (fresh.length) localStorage.setItem(ACHIEVEMENTS_KEY, JSON.stringify(map));
  return fresh;
}

export function readStreakCount(diff) {
  const rec = getJSON(STREAK_KEY_V2(diff), null);
  return Number(rec?.count || 0);
}

const CHALLENGE_ACHIEVEMENT_IDS = new Set([
  'normal_win',
  'hard_win',
  'master_win',
  'speedy',
  'straight_shooter',
  'so_close',
  'dry_feet',
  'quick_trip',
  'sprinter',
  'flawless_line',
  'trailblazer',
  'masterpiece',
  'zero_assist_master',
  'double_perfect',
  'turkey',
  'deep_dive',
]);

export function getAchievementCategory(achievementOrId) {
  const id = typeof achievementOrId === 'string' ? achievementOrId : achievementOrId?.id;
  const explicit = typeof achievementOrId === 'object' ? achievementOrId?.category : null;
  if (explicit) return explicit;
  if (!id) return 'exploration';
  if (id.startsWith('streak')) return 'streak';
  if (CHALLENGE_ACHIEVEMENT_IDS.has(id)) return 'challenge';
  return 'exploration';
}

function isEligibleForRun(achievement, event = {}) {
  const category = getAchievementCategory(achievement);
  if (category === 'challenge') return Number(event?.hintsUsed || 0) === 0;
  if (category === 'streak') return event?.mode === 'daily' || event?.dailyMode === true;
  return true;
}

const FERRY_CROSSING_ACHIEVEMENTS = [
  {
    id: 'ferry_belfast_cairnryan',
    name: 'Belfast Boat',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['BT', 'DG'],
    description: 'Use the Belfast to Cairnryan ferry route',
  },
  {
    id: 'ferry_belfast_douglas',
    name: 'Manx Crossing',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['BT', 'IM'],
    description: 'Use the Belfast to Douglas ferry route',
  },
  {
    id: 'ferry_douglas_liverpool',
    name: 'Liverbird Landing',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['IM', 'L'],
    description: 'Use the Douglas to Liverpool ferry route',
  },
  {
    id: 'ferry_douglas_heysham',
    name: 'Heysham Harbour',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['IM', 'LA'],
    description: 'Use the Douglas to Heysham ferry route',
  },
  {
    id: 'ferry_aberdeen_shetland',
    name: 'North Sea Night Boat',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['AB', 'ZE'],
    description: 'Use the Aberdeen to Shetland ferry route',
  },
  {
    id: 'ferry_aberdeen_orkney',
    name: 'Orkney Passage',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['AB', 'KW'],
    description: 'Use the Aberdeen to Orkney ferry route',
  },
  {
    id: 'ferry_orkney_shetland',
    name: 'Island Chain',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['KW', 'ZE'],
    description: 'Use the Orkney to Shetland ferry route',
  },
  {
    id: 'ferry_outer_hebrides_north',
    name: 'Hebridean Hop',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['IV', 'HS'],
    description: 'Use the Highlands to Outer Hebrides ferry route',
  },
  {
    id: 'ferry_outer_hebrides_oban',
    name: 'Barra Bound',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['PA', 'HS'],
    description: 'Use the Oban to Outer Hebrides ferry route',
  },
  {
    id: 'ferry_outer_hebrides_mallaig',
    name: 'Mallaig Mailboat',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['PH', 'HS'],
    description: 'Use the Mallaig to Outer Hebrides ferry route',
  },
  {
    id: 'ferry_arran_kintyre',
    name: 'Arran Run',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['KA', 'PA'],
    description: 'Use the Arran to Kintyre ferry route',
  },
  {
    id: 'mersey',
    name: 'Ferry Cross the Mersey',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['CH', 'L'],
    description: 'Use the ferry across the Mersey in either direction',
  },
  {
    id: 'ferry_cross_the_thames',
    name: 'Ferry Cross the Thames',
    icon: '⛴️',
    tier: 'bronze',
    hidden: true,
    edge: ['E', 'SE'],
    description: 'Use the Woolwich Ferry across the Thames',
  },
  {
    id: 'ferry_poole_guernsey',
    name: 'Guernsey from Poole',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['BH', 'GY'],
    description: 'Use the Poole to Guernsey ferry route',
  },
  {
    id: 'ferry_poole_jersey',
    name: 'Jersey from Poole',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['BH', 'JE'],
    description: 'Use the Poole to Jersey ferry route',
  },
  {
    id: 'ferry_portsmouth_guernsey',
    name: 'Portsmouth to Guernsey',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['PO', 'GY'],
    description: 'Use the Portsmouth to Guernsey ferry route',
  },
  {
    id: 'ferry_portsmouth_jersey',
    name: 'Portsmouth to Jersey',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['PO', 'JE'],
    description: 'Use the Portsmouth to Jersey ferry route',
  },
  {
    id: 'ferry_guernsey_jersey',
    name: 'Island Shuttle',
    icon: '⛴️',
    tier: 'silver',
    hidden: true,
    edge: ['GY', 'JE'],
    description: 'Use the Guernsey to Jersey ferry route',
  },
];

const BRIDGE_CROSSING_ACHIEVEMENTS = [
  {
    id: 'crossing_dartford',
    name: 'Dartford Crossing',
    icon: '🌉',
    tier: 'bronze',
    hidden: true,
    edge: ['DA', 'RM'],
    description: 'Use the Dartford Crossing',
  },
  {
    id: 'crossing_severn',
    name: 'Severn Span',
    icon: '🌉',
    tier: 'bronze',
    hidden: true,
    edge: ['BS', 'NP'],
    description: 'Use a Severn crossing',
  },
  {
    id: 'crossing_mersey_tunnel',
    name: 'Under the Mersey',
    icon: '🕳️',
    tier: 'bronze',
    hidden: true,
    edge: ['L', 'CH'],
    description: 'Use a Mersey tunnel',
  },
  {
    id: 'crossing_humber',
    name: 'Humber Bridge',
    icon: '🌉',
    tier: 'silver',
    hidden: true,
    edge: ['HU', 'DN'],
    description: 'Use the Humber Bridge',
  },
  {
    id: 'crossing_forth',
    name: 'Forth Crossing',
    icon: '🌉',
    tier: 'silver',
    hidden: true,
    edge: ['EH', 'KY'],
    description: 'Use a Forth crossing',
  },
  {
    id: 'crossing_blackwall',
    name: 'Blackwall Passage',
    icon: '🕳️',
    tier: 'bronze',
    hidden: true,
    edge: ['E', 'SE'],
    description: 'Use the Blackwall Tunnel',
  },
];

const CROSSING_ACHIEVEMENTS = [
  ...FERRY_CROSSING_ACHIEVEMENTS.map((a) => ({ ...a, crossingType: 'ferry', check: () => false })),
  ...BRIDGE_CROSSING_ACHIEVEMENTS.map((a) => ({ ...a, crossingType: 'bridge', check: () => false })),
];

export function unlockCrossingAchievementsForEdge(a, b, { ferry = false, bridge = false } = {}) {
  const edge = canonEdge(a, b);
  if (!edge) return [];

  const want = [];
  if (ferry) {
    want.push(...FERRY_CROSSING_ACHIEVEMENTS.filter((achievement) => (
      canonEdge(...achievement.edge) === edge
    )));
  }
  if (bridge) {
    want.push(...BRIDGE_CROSSING_ACHIEVEMENTS.filter((achievement) => (
      canonEdge(...achievement.edge) === edge
    )));
  }

  return unlockAndPersist(want);
}

// ---- Definitions ----
export const achievements = [
{ id: 'first', name: 'First Steps', icon:'⭐', tier:'bronze',
  description: 'Finish your first game',
  check: (e, h) => e.won && (h.totalWins ?? 0) >= 1 },

  { id: 'normal_win',  name: 'Cartographer', icon:'🧭', tier:'bronze',
    description: 'Win on Normal',
    check: (e) => e.won && e.difficulty==='normal' },

  { id: 'hard_win',    name: 'Pathfinder',   icon:'🗺️', tier:'silver',
    description: 'Win on Hard',
    check: (e) => e.won && e.difficulty==='hard' },

  { id: 'master_win',  name: 'Postcode Master', icon:'📮', tier:'gold',
    description: 'Win on Master',
    check: (e) => e.won && e.difficulty==='master' },

  { id: 'speedy',      name: 'Commuter',     icon:'🚆', tier:'silver',
    description: 'Win using the optimal number of moves',
    check: (e) => e.won && Number.isFinite(e.optimalMoves) && e.moves === e.optimalMoves },

  { id: 'ferry_win',   name: 'Mariner',      icon:'⛴️', tier:'bronze',
    description: 'Use at least two ferries in a winning route',
    check: (e) => e.won && (e.ferryCount ?? 0) >= 2 },

  { id: 'bridge_win',  name: 'Engineer',     icon:'🌉', tier:'bronze',
    description: 'Use at least two bridges/tunnels in a winning route',
    check: (e) => e.won && (e.bridgeCount ?? 0) >= 2 },

  { id: 'centurion',   name: 'Die-hard',     icon:'💯', tier:'legendary',
    description: 'Win 100 games',
    check: (_e,h) => (h.totalWins ?? 0) >= 100 },

  { id: 'visit_25', name: 'Explorer: 25%', icon:'🧭', tier:'bronze',
    description: 'Visit 25% of all postcode areas',
    check: (_e,_h,meta) => (meta?.totalAreas > 0) && (meta.visitedCount / meta.totalAreas >= 0.25) },

  { id: 'visit_50', name: 'Explorer: 50%', icon:'🗺️', tier:'silver',
    description: 'Visit 50% of all postcode areas',
    check: (_e,_h,meta) => (meta?.totalAreas > 0) && (meta.visitedCount / meta.totalAreas >= 0.50) },

  { id: 'visit_100', name: 'Explorer: All Areas', icon:'🌐', tier:'legendary',
    description: 'Visit every postcode area',
    check: (_e,_h,meta) => (meta?.totalAreas > 0) && (meta.visitedCount >= meta.totalAreas) },

  ...CROSSING_ACHIEVEMENTS,

  { id: 'all_ferries', name: 'Harbour Master', icon:'⚓', tier:'gold',
    description: 'Use every ferry route at least once (across all games)',
    check: (_e,_h,meta) => (meta?.totalFerries ?? 0) > 0 && (meta.usedFerriesCount >= meta.totalFerries) },

  { id: 'all_bridges', name: 'Civil Engineer', icon:'🏗️', tier:'gold',
    description: 'Use every bridge/tunnel at least once (across all games)',
    check: (_e,_h,meta) => (meta?.totalBridges ?? 0) > 0 && (meta.usedBridgesCount >= meta.totalBridges) },

  { id: 'lejog', name: 'Land’s End to John O’Groats', icon:'🏁', tier:'silver',
    description: 'Start in TR and finish in KW in a single game',
    check: (e) => e.won && e.startArea === 'TR' && e.endArea === 'KW' },

  { id: 'shortcut', name: 'You call that a shortcut?', icon:'🌀', tier:'silver', hidden:true,
    description: 'Path contains L → IM → BT → DG (or reverse) in one game',
    check: (e) => e.won && Array.isArray(e.pathUsed) &&
      (pathHasSequence(e.pathUsed, ['L','IM','BT','DG']) || pathHasSequence(e.pathUsed, ['DG','BT','IM','L'])) },

  { id: 'straight_shooter', name: 'Straight Shooter', icon:'🎯', tier:'bronze',
    description: 'Win without revisiting any area',
    check: (e) => e.won && Array.isArray(e.pathUsed) &&
      new Set(e.pathUsed).size === e.pathUsed.length },

  { id: 'so_close', name: 'So Close', icon:'➕', tier:'bronze',
    description: 'Win in exactly optimal +1 moves',
    check: (e) => e.won && Number.isFinite(e.optimalMoves) && e.moves === e.optimalMoves + 1 },

  { id: 'dry_feet', name: 'Dry Feet', icon:'🥿', tier:'bronze',
    description: 'Win without using any ferries or bridges',
    check: (e) => e.won && (e.ferryCount ?? 0) === 0 && (e.bridgeCount ?? 0) === 0 },

  { id: 'quick_trip', name: 'Quick Trip', icon:'⏱️', tier:'bronze',
    description: 'Win in under 2 minutes',
    check: (e) => e.won && (e.durationMs ?? Infinity) <= 120000 },

  { id: 'first_crossing', name: 'First Crossing', icon:'🚢', tier:'bronze',
    description: 'Use any ferry route at least once (lifetime)',
    check: (_e,_h,meta) => (meta?.usedFerriesCount ?? 0) >= 1 },

  { id: 'first_span', name: 'First Span', icon:'🧱', tier:'bronze',
    description: 'Use any bridge/tunnel at least once (lifetime)',
    check: (_e,_h,meta) => (meta?.usedBridgesCount ?? 0) >= 1 },

  { id: 'bridge_club', name: 'Bridge Club', icon:'🌉', tier:'silver',
    description: 'Win using 3+ bridges/tunnels',
    check: (e) => e.won && (e.bridgeCount ?? 0) >= 3 },

  { id: 'ferry_captain', name: 'Ferry Captain', icon:'⛴️', tier:'silver',
    description: 'Win using 3+ ferries',
    check: (e) => e.won && (e.ferryCount ?? 0) >= 3 },

  { id: 'marathoner', name: 'Marathoner', icon:'🥾', tier:'silver',
    description: 'Win with a route of 12+ moves',
    check: (e) => e.won && (e.moves ?? 0) >= 12 },

  { id: 'sprinter', name: 'Sprinter', icon:'🏃', tier:'silver',
    description: 'Win in under 60 seconds',
    check: (e) => e.won && (e.durationMs ?? Infinity) <= 60000 },

  { id: 'flawless_line', name: 'Flawless Line', icon:'🧵', tier:'silver',
    description: 'Optimal win with no revisits',
    check: (e) => e.won && Number.isFinite(e.optimalMoves) &&
      e.moves === e.optimalMoves &&
      Array.isArray(e.pathUsed) &&
      new Set(e.pathUsed).size === e.pathUsed.length },

  { id: 'sea_and_stone', name: 'Sea & Stone', icon:'⚓', tier:'silver',
    description: 'Win using both a ferry and a bridge',
    check: (e) => e.won && (e.ferryCount ?? 0) > 0 && (e.bridgeCount ?? 0) > 0 },

  { id: 'trailblazer', name: 'Trailblazer', icon:'🧭', tier:'gold',
    description: 'Win on Hard with no ferries or bridges',
    check: (e) => e.won && e.difficulty==='hard' && (e.ferryCount ?? 0)===0 && (e.bridgeCount ?? 0)===0 },

  { id: 'masterpiece', name: 'Masterpiece', icon:'🎨', tier:'gold',
    description: 'Win on Master with optimal moves',
    check: (e) => e.won && e.difficulty==='master' && Number.isFinite(e.optimalMoves) && e.moves === e.optimalMoves },

  { id: 'explorer_75', name: 'Explorer: 75%', icon:'🧭', tier:'gold',
    description: 'Visit 75% of all postcode areas',
    check: (_e,_h,meta) => (meta?.totalAreas > 0) && (meta.visitedCount / meta.totalAreas >= 0.75) },

  { id: 'networker', name: 'Networker', icon:'🔗', tier:'gold',
    description: 'Use at least half of all ferries and half of all bridges (lifetime)',
    check: (_e,_h,meta) => {
      const tf = meta?.totalFerries ?? 0, tb = meta?.totalBridges ?? 0;
      if (tf === 0 || tb === 0) return false;
      return (meta.usedFerriesCount ?? 0) >= Math.ceil(tf/2) &&
             (meta.usedBridgesCount ?? 0) >= Math.ceil(tb/2);
    } },

  { id: 'grand_tour', name: 'Grand Tour', icon:'🎒', tier:'legendary',
    description: 'Win with a route of 20+ moves',
    check: (e) => e.won && (e.moves ?? 0) >= 20 },

{ id: 'zero_assist_master', name: 'Zero-Assist Master', icon:'🧘',
  tier:'legendary',
  description: 'Win on Master with zero hints',
  check: (e) => e.won && e.difficulty === 'master' && (e.hintsUsed ?? 0) === 0 },

  { id: 'double_perfect', name: 'Double Perfect', icon:'✨', tier:'gold',
  description: 'Two consecutive wins with optimal moves',
  check: () => lastNGamesArePerfect(2) },

{ id: 'turkey', name: 'Turkey', icon:'✨', tier:'legendary',
  description: 'Three consecutive wins with optimal moves',
  check: () => lastNGamesArePerfect(3) },

  { id: 'infrastructure_chief', name: 'Infrastructure Chief', icon:'🏆', tier:'legendary',
    description: 'Use every ferry AND every bridge at least once (lifetime)',
    check: (_e,_h,meta) => {
      const have = getJSON(ACHIEVEMENTS_KEY, {}) || {};
      if (have.all_ferries && have.all_bridges) return true;
      const tf = meta?.totalFerries ?? 0, tb = meta?.totalBridges ?? 0;
      if (tf === 0 || tb === 0) return false;
      return (meta.usedFerriesCount >= tf) && (meta.usedBridgesCount >= tb);
    } },

    // ---- Easy streaks ----
{ id: 'streak7_easy',  name: 'First-Class Week: Easy',  icon:'🔥', tier:'bronze',
  description: '7-day daily streak on Easy',
  check: () => readStreakCount('easy') >= 7 },

{ id: 'streak14_easy', name: 'Fortnight Postmark: Easy', icon:'🔥', tier:'silver',
  description: '14-day daily streak on Easy',
  check: () => readStreakCount('easy') >= 14 },

{ id: 'streak30_easy', name: 'Monthly Round: Easy', icon:'🔥', tier:'gold',
  description: '30-day daily streak on Easy',
  check: () => readStreakCount('easy') >= 30 },

// ---- Normal streaks ----
{ id: 'streak7_normal',  name: 'First-Class Week: Normal',  icon:'🔥', tier:'bronze',
  description: '7-day daily streak on Normal',
  check: () => readStreakCount('normal') >= 7 },

{ id: 'streak14_normal', name: 'Fortnight Postmark: Normal', icon:'🔥', tier:'silver',
  description: '14-day daily streak on Normal',
  check: () => readStreakCount('normal') >= 14 },

{ id: 'streak30_normal', name: 'Monthly Round: Normal', icon:'🔥', tier:'gold',
  description: '30-day daily streak on Normal',
  check: () => readStreakCount('normal') >= 30 },

// ---- Hard streaks ----
{ id: 'streak7_hard',  name: 'First-Class Week: Hard',  icon:'🔥', tier:'bronze',
  description: '7-day daily streak on Hard',
  check: () => readStreakCount('hard') >= 7 },

{ id: 'streak14_hard', name: 'Fortnight Postmark: Hard', icon:'🔥', tier:'silver',
  description: '14-day daily streak on Hard',
  check: () => readStreakCount('hard') >= 14 },

{ id: 'streak30_hard', name: 'Monthly Round: Hard', icon:'🔥', tier:'gold',
  description: '30-day daily streak on Hard',
  check: () => readStreakCount('hard') >= 30 },

// ---- Master streaks ----
{ id: 'streak7_master',  name: 'First-Class Week: Master',  icon:'🔥', tier:'bronze',
  description: '7-day daily streak on Master',
  check: () => readStreakCount('master') >= 7 },

{ id: 'streak14_master', name: 'Fortnight Postmark: Master', icon:'🔥', tier:'silver',
  description: '14-day daily streak on Master',
  check: () => readStreakCount('master') >= 14 },

{ id: 'streak30_master', name: 'Monthly Round: Master', icon:'🔥', tier:'gold',
  description: '30-day daily streak on Master',
  check: () => readStreakCount('master') >= 30 },

// 365 streak
{ id: 'streak_365', name: 'The Grand Route', icon:'🔥', tier:'legendary',
  description: 'Daily streak of 365 (any difficulty)',
  check: (_e,_h,_meta) => ['easy','normal','hard','master'].some(d => readStreakCount(d) >= 365) },

 
  { id: 'island_hopper', name: 'Island Hopper', icon:'🏝️', tier:'silver', hidden:true,
    description: 'In one game, visit IM, JE and GY',
    check: (e) => e.won && Array.isArray(e.pathUsed) && ['IM','JE','GY'].every(x => e.pathUsed.includes(x)) },

  { id: 'there_and_back_again', name: 'There and Back Again', icon:'🔁', tier:'gold', hidden:true,
    description: 'Return to your start area before finishing (and still win)',
    check: (e) => {
      if (!e.won || !Array.isArray(e.pathUsed) || !e.startArea) return false;
      const mid = e.pathUsed.slice(1,-1);
      return mid.includes(e.startArea);
    } },

  { id: 'all_aboard', name: 'All Aboard!', icon:'🛳️', tier:'gold', hidden:true,
    description: 'Use 5+ ferries in a single win',
    check: (e) => e.won && (e.ferryCount ?? 0) >= 5 },

  { id: 'tunneller', name: 'Tunneller', icon:'🕳️', tier:'gold', hidden:true,
    description: 'Use 5+ bridges/tunnels in a single win',
    check: (e) => e.won && (e.bridgeCount ?? 0) >= 5 },

  { id: 'unlucky_13', name: 'Unlucky for Some', icon:'🎲', tier:'silver', hidden:true,
    description: 'Win in exactly 13 moves',
    check: (e) => e.won && e.moves === 13 },

  { id: 'lejog_plus', name: 'LeJoG+', icon:'🏁', tier:'legendary', hidden:true,
    description: 'TR → KW in 15+ moves',
    check: (e) => e.won && e.startArea==='TR' && e.endArea==='KW' && (e.moves ?? 0) >= 15 },


  {
    id: 'backtrack',
    name: 'Backtrack',
    icon: '↩️',
    tier: 'bronze',
    description: 'Win a game where you enter the same area twice.',
    check: (e) => e.won && Array.isArray(e.pathUsed) && e.pathUsed.length !== new Set(e.pathUsed).size,
  },
  {
    id: 'long_distance',
    name: 'Long-Distance',
    icon: '📏',
    tier: 'bronze',
    description: 'Win a game where the optimal path is 10+ moves.',
    check: (e) => e.won && (e.optimalMoves ?? 0) >= 10,
  },
  
  {
    id: 'scenic_route',
    name: 'The Scenic Route',
    icon: '🌸',
    tier: 'silver',
    description: 'Win with at least 5 more moves than the optimal path.',
    check: (e) => e.won && Number.isFinite(e.optimalMoves) && (e.moves ?? 0) >= e.optimalMoves + 5,
  },
  {
    id: 'first_last',
    name: 'First and Last',
    icon: '📚',
    tier: 'silver',
    description: 'Start and finish with the same initial letter.',
    check: (e) => e.won && e.startArea?.[0] && (e.startArea[0] === e.endArea?.[0]),
  },
  
  {
    id: 'deep_dive',
    name: 'Deep Dive',
    icon: '🤿',
    tier: 'gold',
    description: 'Win a game on Hard difficulty, using 5+ moves.',
    check: (e) => e.won && e.difficulty === 'hard' && (e.moves ?? 0) >= 5,
  },
  {
    id: 'half_century',
    name: 'Half-Century',
    icon: '5️⃣0️⃣',
    tier: 'gold',
    description: 'Play 50 games.',
    check: (_e, h) => (h.totalGames ?? 0) >= 50,
  },


  
  ].map((a) => ({ ...a, category: getAchievementCategory(a) }));

// unlock helper that emits *both* global and per-difficulty ids
export function unlockStreaksFor(diff) {
  const n = readStreakCount(diff);
  const want = [];

  if (n >= 365) want.push({ id: 'streak_365', name: 'The Grand Route', tier: 'legendary' });

  // per-difficulty thresholds
  const titleCase = s => s.charAt(0).toUpperCase() + s.slice(1);
  const label = titleCase(diff);

  if (n >= 7)  want.push({ id: `streak7_${diff}`,  name: `First-Class Week: ${label}`, tier: 'bronze' });
  if (n >= 14) want.push({ id: `streak14_${diff}`, name: `Fortnight Postmark: ${label}`, tier: 'silver' });
  if (n >= 30) want.push({ id: `streak30_${diff}`, name: `Monthly Round: ${label}`, tier: 'gold' });

  return unlockAndPersist(want); // your existing persist that returns only *new* ones
}

export function readStreakRecord(diff) {
  try {
    return JSON.parse(localStorage.getItem(STREAK_KEY_V2(diff)) || 'null')
        || { count: 0, lastWinDate: null };
  } catch {
    return { count: 0, lastWinDate: null };
  }
}

const STREAK_TIERS = [
  { n: 3,  id: 'streak_3',  name: 'Threepeat',        tier: 'bronze' },
  { n: 7,  id: 'streak_7',  name: 'One Week Wonder',  tier: 'silver' },
  { n: 14, id: 'streak_14', name: 'Fortnight Flyer',  tier: 'gold' },
  { n: 30, id: 'streak_30', name: 'Monthly Machine',  tier: 'gold' },
];

export function checkAndUnlockStreakAchievements(diff) {
  const { count } = readStreakRecord(diff);
  const want = STREAK_TIERS.filter(t => count >= t.n);
  return unlockAndPersist(want); // must return only *newly* unlocked
}

export const evaluateAndUnlockMetaAchievements = (...args) =>
  evaluateAndUnlockAchievements(...args);

// ---- Evaluators ----
export function evaluateAndUnlockAchievements(
  event,
  history = null,
  meta = null
) {
  // ✅ ensure we have real history/meta when not provided
  history = buildHistoryIfMissing(history);
  meta    = buildMetaIfMissing(meta);

  const have = readJSON(ACHIEVEMENTS_KEY, {}); // { [id]: rec }
  const nowISO = new Date().toISOString();
  const newly = [];

  for (const a of achievements) {
    try {
      if (!have[a.id] && isEligibleForRun(a, event) && a.check(event, history, meta)) {
        have[a.id] = { ...a, category: getAchievementCategory(a), unlockedAt: nowISO };
        newly.push(have[a.id]);
      }
    } catch {
      // swallow single-achievement errors
    }
  }

  if (newly.length) writeJSON(ACHIEVEMENTS_KEY, have);
  return newly;
}



// ---- Analytics helper ----
export function logAchievementsToGA(unlocked, context) {
  if (!window.gtag || !unlocked?.length) return;
  unlocked.forEach(a => {
    window.gtag('event', 'unlock_achievement', {
      achievement_id: a.id,
      tier: a.tier,
      category: a.category || 'standard',
      difficulty: context.difficulty,
      daily_mode: context.dailyMode ? 1 : 0,
      ferry_count: context.ferryCount ?? 0,
      bridge_count: context.bridgeCount ?? 0,
    });
  });
}



const USED_FERRIES_KEY = 'pp_used_ferries_v1';
const USED_BRIDGES_KEY = 'pp_used_bridges_v1';

export function checkAndUnlockMetaAchievements(_difficulty, _postcodeAreas, ferryLinks = [], bridgeLinks = []) {
  // read used edges from LS
  const usedF = new Set(getJSON(USED_FERRIES_KEY, []));
  const usedB = new Set(getJSON(USED_BRIDGES_KEY, []));

  // compute totals from the arrays you pass in
  const allF = new Set(ferryLinks.map(({a,b}) => canonEdge(a,b)));
  const allB = new Set(bridgeLinks.map(({a,b}) => canonEdge(a,b)));

  const want = [];
  if (usedF.size >= 1) want.push({ id: 'first_crossing' });
if (usedB.size >= 1) want.push({ id: 'first_span' });

if (allF.size > 0 && usedF.size === allF.size) want.push({ id: 'all_ferries' });
if (allB.size > 0 && usedB.size === allB.size) want.push({ id: 'all_bridges' });

if (
  allF.size > 0 &&
  allB.size > 0 &&
  usedF.size >= Math.ceil(allF.size / 2) &&
  usedB.size >= Math.ceil(allB.size / 2)
) {
  want.push({ id: 'networker' });
}

if (
  allF.size > 0 &&
  allB.size > 0 &&
  usedF.size === allF.size &&
  usedB.size === allB.size
) {
  want.push({ id: 'infrastructure_chief' });
}

const have = getJSON(ACHIEVEMENTS_KEY, {}) || {};
if (have.all_ferries && have.all_bridges) {
  want.push({ id: 'infrastructure_chief' });
}

  // map the minimal items above to real objects from your defined list (by id)
  const byId = new Map(achievements.map(a => [a.id, a]));
  const resolved = want.map(w => byId.get(w.id)).filter(Boolean);

  return unlockAndPersist(resolved);
}
